import {Component} from "react"
import Helmet from "react-helmet"
import ErrorHandler from "../errors/ErrorHandler"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import ApiHandler from "src/api/ApiHandler"
import UserVisibleErrorStoringDecorator from "src/components/decorators/UserErrorStoringDecorator"
import StateConstants from "src/states/StateContstants"
import Loader from "src/components/general/Loading"
import ValuationLoader from "src/components/general/ValuationLoader"
import ComparableHomesWidget from "../comparable_homes/ComparableHomesWidget"
import AddressMap from "../maps/AddressMap"
import SearchForm from "src/components/search/SearchForm"
import HouseForm from "../house_valuation/HouseForm"
import ValuationPageSEO from "./ValuationPageSEO"
import ValuationBlockedMessage from "./ValuationBlockedMessage"
import FeatureFlagIds from "src/features/FeatureFlagIds"
import "./ValuationPage.css"
import ValuationStateHelper from "src/utils/ValuationStateHelper"
import NoValuationPage from "./NoValuationPage"
import PremiumBanner from "src/components/premium/PremiumBanner"
import posthog from "posthog-js"
import ValuationHistorySection from "../house_valuation/ValuationHistorySection"

class ValuationPage extends Component<any> {
    propertyId: string
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    errorHandler: ErrorHandler
    loader: Loader
    state: any
    hasValuated: boolean = false
    lastPropertyId: string | null = null

    constructor(props: any) {
        super(props)
        this.propertyId = props.propertyId
        this.lastPropertyId = props.propertyId
        this.stateHandler = props.stateHandler
        this.apiHandler = props.apiHandler
        this.errorHandler = props.errorHandler
        this.loader = props.loader
        this.state = {
            isLoading: true,
            shouldUpdateValuation: false
        }
        this.stateHandler.addListener(StateConstants.SHOULD_UPDATE_VALUATION, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                shouldUpdateValuation: state
            }))
        })
        this.stateHandler.addListener(StateConstants.IS_LOADING, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                isLoading: state
            }))
        })
        this.stateHandler.addListener(StateConstants.VALUATION_STATE, (state: any) => {
            this.setState((prevState) => ({
                valuation: state
            }))
        })
        this.stateHandler.addListener(StateConstants.ERROR, () => {
            this.setState({})
        })
    }

    async componentDidMount() {
        await this.loadValuationIfNeeded()
    }

    async loadSavedValuation() {
        try {
            let savedValuations = await this.apiHandler.listValuations({
                limit_per_page: 50,
                include_items: true
            }, true)
            this.stateHandler.set(StateConstants.SAVED_VALUATIONS, savedValuations)
        } catch (error) {
            console.error("Error getting saved valuation:", error)
        }
    }

    async componentDidUpdate(prevProps: any) {
        // Check if propertyId has changed (route change)
        if (prevProps.propertyId !== this.props.propertyId) {
            this.propertyId = this.props.propertyId
            this.lastPropertyId = this.props.propertyId
            this.hasValuated = false
            // Clear old valuation state when property changes
            this.stateHandler.set(StateConstants.VALUATION_STATE, null)
            this.stateHandler.set(StateConstants.COMPARABLE_HOMES_STATE, null)
            this.setState({ isLoading: true, valuation: null })
            await this.loadValuationIfNeeded()
            return
        }

        // Check for manual update trigger
        let shouldUpdateValuation = this.stateHandler.get(StateConstants.SHOULD_UPDATE_VALUATION)
        if (shouldUpdateValuation) {
            this.stateHandler.set(StateConstants.SHOULD_UPDATE_VALUATION, false)
            await this.valuate()
        }
    }

    isPropertyBlocked(propertyId: string): boolean {
        const featureProvider = this.props.featureProvider
        if (!featureProvider) return false
        const blocklist = featureProvider.getFeatureFlag(FeatureFlagIds.VALUATION_BLOCKLIST).value
        return Array.isArray(blocklist) && blocklist.includes(propertyId)
    }

    async loadValuationIfNeeded() {
        if (!this.hasValuated && this.propertyId) {
            this.hasValuated = true
            if (this.isPropertyBlocked(this.propertyId)) {
                this.setState({ isLoading: false })
                return
            }
            if (!this.stateHandler.get(StateConstants.VALUATION_USAGE)) {
                try {
                    const usage = await this.apiHandler.getValuationUsage()
                    if (usage) {
                        this.stateHandler.set(StateConstants.VALUATION_USAGE, usage)
                    }
                } catch (e) {
                    console.error("Failed to fetch valuation usage", e)
                }
            }
            await this.valuate()
            try {
                const freshUsage = await this.apiHandler.getValuationUsage()
                if (freshUsage) {
                    this.stateHandler.set(StateConstants.VALUATION_USAGE, freshUsage)
                    if (freshUsage.is_limit_exceeded) {
                        posthog?.capture("valuation_limit_reached", {
                            quota: freshUsage.limit,
                            quota_resets_at: freshUsage.quota_resets_at ?? null,
                        })
                    }
                }
            } catch (e) {
                console.error("Failed to refresh valuation usage", e)
            }
            await this.loadSavedValuation()
            this.setState({ isLoading: false })
        }
    }

    render() {
        const { isLoading } = this.state
        const error = this.stateHandler.get("error")

        if (isLoading) {
            return <ValuationLoader />
        }

        if (error) {
            if (ErrorHandler.isNoNearbyHomesError(error)) {
                return <NoValuationPage />
            }
            return this.errorHandler.render(error, {
                onDismiss: () => this.stateHandler.set(StateConstants.ERROR, null)
            })
        }

        if (this.isPropertyBlocked(this.propertyId)) {
            return (
                <div>
                    <Helmet>
                        <meta name="robots" content="noindex, nofollow" />
                    </Helmet>
                    {this.errorHandler.render(this.stateHandler.get("error"), {
                        onDismiss: () => this.stateHandler.set(StateConstants.ERROR, null)
                    })}
                    <ValuationBlockedMessage />
                </div>
            )
        }

        const inlineError = this.stateHandler.get("error")
        return (
            <div>
                {inlineError && !ErrorHandler.isNoNearbyHomesError(inlineError) && this.errorHandler.render(inlineError, {
                    onDismiss: () => this.stateHandler.set(StateConstants.ERROR, null)
                })}
                <ValuationPageContent 
                    propertyId={this.propertyId} 
                    stateHandler={this.stateHandler} 
                    apiHandler={this.apiHandler} 
                    errorHandler={this.errorHandler}
                    loginHelper={this.props.loginHelper}
                    loader={this.loader}
                    featureProvider={this.props.featureProvider}
                />
            </div>
        )
    }

    @UserVisibleErrorStoringDecorator(StateConstants.VALUATION_LOADING)
    async valuate() {
        let comparableHomesOutput = await this.apiHandler.getComparableHomes({"id": this.propertyId})
        if (comparableHomesOutput["comparable_addresses"].length === 0) {
            throw new Error(`No nearby sold homes were found for address '${comparableHomesOutput["address"]['a']}'`)
        }
        this.stateHandler.set(StateConstants.COMPARABLE_HOMES_STATE, comparableHomesOutput)
        ValuationStateHelper.removeComparableHomes(this.stateHandler, this.stateHandler.get(StateConstants.REMOVED_HOMES))
        let valuation = await this.apiHandler.getValuation(comparableHomesOutput)
        this.stateHandler.set(StateConstants.VALUATION_STATE, valuation)
    }
}

export default ValuationPage

class ValuationPageContent extends Component<any> {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    errorHandler: ErrorHandler
    loginHelper: any
    loader: Loader
    featureProvider: any

    constructor(props: any) {
        super(props)
        this.stateHandler = props.stateHandler
        this.apiHandler = props.apiHandler
        this.errorHandler = props.errorHandler
        this.loginHelper = props.loginHelper
        this.loader = props.loader
        this.featureProvider = props.featureProvider
        this.stateHandler.addListener(StateConstants.IS_MOBILE, () => {
            this.setState({})
        })
    }

    handleValuationChange = (newValuation: number) => {
        let currentValuation = this.stateHandler.get(StateConstants.VALUATION_STATE)
        currentValuation["v"] = newValuation
        this.stateHandler.set(StateConstants.VALUATION_STATE, currentValuation)
    }

    render() {
        let valuation = this.stateHandler.get(StateConstants.VALUATION_STATE)
        if (!valuation) {
            return <ValuationLoader />
        }
        
        let address = valuation["a"] || ""
        let displayAddress = valuation["da"] || address

        const timeSeries = valuation ? valuation["vts"] : null
        const isMobile = this.stateHandler.get(StateConstants.IS_MOBILE)

        return (
            <div>
                <ValuationPageSEO
                    propertyId={this.props.propertyId}
                    valuation={valuation}
                    stateHandler={this.stateHandler}
                />
                <div className="valuation-page-container">
                    <div className="valuation-container">
                    <HouseForm
                        apiHandler={this.apiHandler}
                        address={address}
                        displayAddress={displayAddress}
                        loader={this.loader}
                        stateHandler={this.stateHandler}
                        loginHelper={this.loginHelper}
                        featureProvider={this.featureProvider}
                    />
                    <div className="house-form-container map-form-container">
                        <div className="address-container">
                            <div id="property-map-container" className="property-map-container">
                                <AddressMap stateHandler={this.stateHandler}/>
                            </div>
                            <div className="valuation-data-partition">
                                <div className="comparable-homes-container">
                                    <ComparableHomesWidget stateHandler={this.stateHandler} apiHandler={this.apiHandler}/>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>

                    {isMobile && timeSeries && (
                        <div className="house-form-container valuation-history-container">
                            <div className="widget-header">
                                <h3 className="widget-title">Price Trends</h3>
                                <p className="widget-subtitle">Track how this property's value has changed</p>
                            </div>
                            <ValuationHistorySection
                                timeSeries={timeSeries}
                                stateHandler={this.stateHandler}
                                apiHandler={this.apiHandler}
                            />
                        </div>
                    )}
                </div>
            </div>
        )
    }
}